import cn from "classnames";
import Link from "next/link";
import { useRouter } from "next/router";
import { type FC, useEffect, useMemo, useRef, useState } from "react";
import { match } from "ts-pattern";

import {
	type DocumentMetadata,
	type PublishSettings,
	makeUrlPrefix,
} from "@moment/api-collab/api-types";
import {
	useDocumentFromCurrentRoute,
	usePageFromCurrentRoute,
	useWorkspaceFromCurrentRoute,
} from "@moment/state";
import {
	LoadingSkeleton,
	LoadingSpinner,
	Sidebar,
	SidebarContent,
	SidebarGroup,
	SidebarMenu,
	SidebarProvider,
	SidebarTrigger,
	invokePostMessage,
	isRunningInIframe,
} from "@moment/ui";

import { Editor } from "~/components/canvas/editor/BigRichTextEditor";
import { usePageData } from "~/state";
import { useSelectAllCellsReady } from "~/state/machines/cells";

import { makePageTree } from "../../navigation-pane/makePageTree";
import { Footer } from "../PublishedDocument/Footer";
import { StaticDocumentHead } from "../StaticDocumentHead";
import { PublishHeaderActions } from "../shared/PublishHeaderActions";
import { SidebarMenuItemTree } from "./SidebarMenuItemTree";
import { useScrollThrough } from "./useScrollThrough";
import { useTableOfContents } from "./useTableOfContents";

export type CanvasProps = {
	canvas: DocumentMetadata;
	loading?: boolean;
};

/**
 * This renders a Moment document in the style of a VitePress technical docs website.
 */
export const TemplateVitePressDocs: FC<CanvasProps> = ({ canvas, loading = false }) => {
	// Hooks

	const allCellsReady = useSelectAllCellsReady();
	const [isFirstPageLoad, setIsFirstPageLoad] = useState(true);

	const { workspaceName } = useWorkspaceFromCurrentRoute();
	const { documentId } = useDocumentFromCurrentRoute();
	const { pageMetadata } = usePageFromCurrentRoute();
	const pageData = usePageData(documentId, pageMetadata?.id ?? "");

	const pageTree = useMemo(
		() => makePageTree((canvas?.pagesMetadata ?? []).filter((page) => !page.archived)),
		[canvas.pagesMetadata]
	);

	const showSkeleton = useMemo(() => {
		if (isFirstPageLoad && allCellsReady) {
			/* if we are still on first page load and all cells are initialized then turn off "firstPageLoad" and return false */
			setIsFirstPageLoad(false);
		} else if (isFirstPageLoad && !allCellsReady) {
			/* if we are on first page load and cells are still loading */
			return true;
		}

		return false;
	}, [allCellsReady, isFirstPageLoad]);

	// Refs
	const editorContainerRef = useRef<HTMLDivElement>(null);
	const sidebarScrollRef = useScrollThrough('[data-scroll-container="vitepress-docs"]');

	const { headings, visibleHeading } = useTableOfContents();

	return (
		<>
			{/* ------ Client Meta Tags ------ */}
			<StaticDocumentHead canvas={canvas} pageData={pageData} pageSlug={pageMetadata?.slug} />

			{/* ------ TOP BAR ------ */}
			<div
				className="flex h-screen overflow-y-auto overscroll-auto"
				data-scroll-container="vitepress-docs"
			>
				<SidebarProvider className="h-fit">
					<div className="flex grow bg-secondary w-full max-w-[0%] md:max-w-[35%] lg:max-w-[27%]">
						<div className="ml-auto md:pl-4 h-fit">
							<Sidebar className="!border-r-0 pr-[2rem] pl-1" insetX={false}>
								<div className="flex items-center border-b h-16">
									{canvas.title ? (
										<div
											className="text-md font-semibold pt-5 pl-2 md:p-0"
											title={canvas.title}
										>
											{canvas.title}
										</div>
									) : (
										<div className="text-md font-semibold text-tertiary italic">
											Untitled doc
										</div>
									)}
								</div>

								<SidebarContent ref={sidebarScrollRef} className="py-4 -mx-1 px-1">
									<SidebarGroup className="!px-0">
										<SidebarMenu>
											{}
											<SidebarMenuItemTree
												workspaceName={workspaceName}
												documentId={documentId}
												activePage={pageMetadata}
												pageTree={pageTree}
												publishSettings={canvas.publishSettings}
											/>
										</SidebarMenu>
									</SidebarGroup>
								</SidebarContent>
							</Sidebar>
						</div>
					</div>

					{/* --- DOCUMENT STATE --- */}
					<div
						className={cn(
							"grow",
							"bg-primary",
							"min-h-screen",
							"h-fit",
							"max-w-screen",
							"w-full",
							"sm:w-fit",
							// IMPORTANT: Sidebar should slide *behind* the main content.
							"z-surface",
							// Flex column layout to push footer to bottom
							"flex",
							"flex-col"
						)}
					>
						{/* IMPORTANT: z-component-raised so that this appears over the datagrid. */}
						<div className="max-w-[64rem] sticky top-0 left-0 flex flex-col bg-primary z-component-raised border-b">
							<div className="flex items-center h-16 shrink-0">
								<SidebarTrigger className="ml-4 md:hidden" />
								<PublishHeaderActions
									documentMetadata={canvas}
									className="ml-auto gap-2 flex items-center pr-4 xl:pr-0"
								/>
							</div>
						</div>

						<div className="flex flex-1 max-w-[58rem] mx-4 md:ml-[2rem] lg:ml-[4rem] xl:ml-[6rem] mt-12">
							<main
								className={cn(
									"mr-0",
									"md:mr-[2rem]",
									"lg:mr-[4rem]",
									"shrink",
									"grow",
									"max-w-full",
									// IMPORTANT: Override default scroll margin in
									// _prose-mirror-overrides.css, or clicking the table of
									// contents will hide the heading behind the top bar.
									"[&_[data-text-cell]]:scroll-mt-24",
									headings.length === 0 ? "xl:max-w-[54rem]" : "xl:max-w-[42rem]"
								)}
							>
								<h1 className="mb-12 text-3xl">{pageMetadata?.title}</h1>
								{loading ? (
									<div className="flex w-full justify-center py-12">
										<LoadingSpinner />
									</div>
								) : (
									match({
										showSkeleton,
									})
										.with({ showSkeleton: true }, () => (
											<LoadingSkeleton
												barCount={10}
												randomizeWidths
												showChildren={!showSkeleton}
												SkeletonWrapper={({
													children,
												}: FC["arguments"]["children"]) => (
													<div className="canvas-content-row space-y-4">
														{children}
													</div>
												)}
											/>
										))
										.with({ showSkeleton: false }, () => (
											<Editor
												ref={editorContainerRef}
												publishTemplate="tech-docs-vitepress"
											/>
										))
										.exhaustive()
								)}
							</main>

							{headings.length === 0 ? null : (
								<div className="grow bg-primary hidden xl:block max-w-[12rem] min-w-[12rem]">
									<div className="ml-auto sticky top-28 text-sm flex flex-col max-w-[12rem] max-h-[calc(100vh-7rem)] overflow-y-auto pb-2">
										<>
											<div className="flex items-center gap-2 my-2 font-medium">
												<div>On this page</div>
											</div>
											<div className="relative h-full overflow-y-auto scroll-pt-6 scroll-pb-12">
												<div className="sticky top-0 left-0 right-0 h-6 bg-gradient-to-b from-bg-primary to-transparent pointer-events-none" />
												{headings.map(
													([level, heading, headingId], index) => (
														<SidebarHeading
															key={`${heading}-${index}`}
															level={level}
															documentId={documentId}
															heading={heading}
															headingId={headingId}
															publishSettings={canvas.publishSettings}
															visibleHeading={visibleHeading}
															workspaceName={workspaceName}
														/>
													)
												)}
												<div className="sticky bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-bg-primary to-transparent pointer-events-none" />
											</div>
										</>
									</div>
								</div>
							)}
						</div>

						<Footer />
					</div>
				</SidebarProvider>
			</div>
		</>
	);
};

const SidebarHeading = ({
	level,
	documentId,
	heading,
	headingId,
	publishSettings,
	visibleHeading,
	workspaceName,
}: {
	level: number;
	documentId: string;
	heading: string;
	headingId: string;
	publishSettings: PublishSettings | undefined;
	visibleHeading: string | null;
	workspaceName: string;
}) => {
	const ref = useRef<HTMLDivElement>(null);
	const isVisible = useMemo(() => headingId === visibleHeading, [headingId, visibleHeading]);
	const [urlPrefix, setUrlPrefix] = useState(
		makeUrlPrefix(workspaceName, documentId, publishSettings)
	);
	const router = useRouter();
	useEffect(() => {
		if (!isRunningInIframe()) {
			return;
		}

		invokePostMessage<string>("ping")
			.then((parentOrigin?: string) => {
				if (parentOrigin && urlPrefix.startsWith("/")) {
					setUrlPrefix(
						`${parentOrigin}${makeUrlPrefix(workspaceName, documentId, undefined)}`
					);
				}
			})
			.catch((err) => console.error("Error sending ping request to the parent frame", err));
	}, [router.query, setUrlPrefix, workspaceName, documentId, urlPrefix]);

	/*
	 * When the heading is visible, scroll it into view.
	 */
	useEffect(() => {
		if (isVisible) {
			ref.current?.scrollIntoView({
				behavior: "smooth",
				block: "nearest",
			});
		}
	}, [headingId, isVisible]);

	return (
		<div ref={ref} className="border-l py-1">
			<div
				className={cn("-ml-px", "border-l-[3px]", {
					"font-semibold border-brand": headingId === visibleHeading,
					"border-transparent": headingId !== visibleHeading,
				})}
				style={{
					paddingLeft: `${level}em`,
				}}
			>
				<a
					id={`#${headingId}-link`}
					href={`${urlPrefix}#${headingId}`}
					data-anchor-link={`#${headingId}`}
					onClick={async (e) => {
						e.preventDefault();
						e.stopPropagation();
						const anchorLink = e.currentTarget.dataset["anchorLink"];
						await router.push(anchorLink);
					}}
					className={cn(
						"text-primary",
						"no-underline",
						"hover:underline",
						"active:underline",
						"active:ring-0"
					)}
				>
					{heading}
				</a>
			</div>
		</div>
	);
};
